<template>
  <div
    class="w-full flex flex-col rounded-md h-[48px] px-[18px] pt-1.5 pb-2 relative"
    :class="componentClasses"
  >
    <div
      class="label font-primary font-medium text-[14px] text-gray-800 opacity-50 h-full flex items-center mt-1"
      :class="labelClasses"
    >
      {{ label }}
    </div>
    <div
      v-if="props.modelValue"
      class="font-primary flex items-center text-[14px] text-gray-800 leading-[18px] overflow-hidden"
      :class="contentClasses"
    >
      <div
        v-if="selectedOptionIcon"
        class="min-w-[16px] max-w-[16px] min-h-[16px] max-h-[16px] mr-1"
      >
        <component :is="selectedOptionIcon" class="w-full h-full" />
      </div>
      <div
        class="w-full mr-[34px] whitespace-nowrap text-ellipsis overflow-hidden"
      >
        {{ selectedOptionLabel }}
      </div>
    </div>
    <select
      class="w-full appearance-none bg-transparent outline-hidden absolute top-0 left-0 h-full cursor-pointer text-transparent"
      ref="select"
      @change="handleChange"
    >
      <option value="">--</option>
      <option
        v-for="(option, index) in props.options"
        :key="index"
        :value="option.value"
        :selected="isOptionSelected(option)"
      >
        {{ option.label }}
      </option>
    </select>
    <Icon
      :size="16"
      name="ChevronUp"
      class="rotate-180 absolute top-4 right-[18px] stroke-gray-800 pointer-events-none"
      :class="contentClasses"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Icon } from '@dacr/ui'

type Option = {
  value: string | number
  label: string
  icon?: string
}

const props = defineProps<{
  label: string
  modelValue: string
  options: Option[]
  flat?: boolean
  disabled?: boolean
}>()
const emit = defineEmits(['update:modelValue'])

const select = ref<HTMLSelectElement | null>(null)

const handleChange = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLSelectElement)?.value)
}

const isOptionSelected = (option: Option) => {
  return option.value === props.modelValue
}
const selectedOptionLabel = computed(() => {
  const option = props.options.find(isOptionSelected)

  return option?.label
})

const selectedOptionIcon = computed(() => {
  const option = props.options.find(isOptionSelected)

  return option?.icon
})

const componentClasses = computed(() => ({
  'bg-gray-800/5': props.flat,
  'border border-gray-800/25': !props.flat,
  'pointer-events-none border-gray-800/10! bg-gray-800/3! text-gray-800/40':
    props.disabled,
}))

const labelClasses = computed(() => ({
  'label--mini': props.modelValue,
  ...(props.disabled && {
    'opacity-25': true,
  }),
}))

const contentClasses = computed(() => ({
  ...(props.disabled && {
    'opacity-50': true,
  }),
}))
</script>

<style scoped>
.label {
  transition: all 0.1s ease;
}

.label--mini {
  margin-top: 0px;
  font-size: 11px;
  height: 14px;
}
</style>
